import './style.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { getDetailFile, updateDetailFile, uploadDetailFile } from 'apis/financialReviews.api';
import { HugerteEditor, FileInput, NumberInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import { formatFileName, mapError } from 'utils/func';
import {
  createReviewValidationSchema,
  updateReviewValidationSchema,
} from 'features/Council/validation';
import { toast } from 'layout';

export default function FinancialForm({
  visible,
  setVisible,
  isCreateMode,
  councilMemberId,
  councilId,
  projectProposalId,
  handleDownloadReviewFile,
  setIsConfirmSubmitModalOpen,
  refetchFinancial,
  refetchCouncil,
}) {
  // #region Data
  const { t } = useTranslation();

  const formOptions = {
    resolver: yupResolver(
      isCreateMode ? createReviewValidationSchema : updateReviewValidationSchema
    ),
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm(formOptions);

  const { isDirty, dirtyFields } = useFormState({
    control,
  });
  const { data: reviewData } = useQuery(
    ['Reviews', councilMemberId, councilId, projectProposalId],
    ({ queryKey: [, _councilMemberId, _councilId, _projectProposalId] }) =>
      getDetailFile(_councilMemberId, _councilId, _projectProposalId),
    { enabled: !!councilMemberId && !isCreateMode }
  );
  const review = useMemo(() => (reviewData?.data !== '' ? reviewData?.data : null), [reviewData]);

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(uploadDetailFile);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(updateDetailFile);

  useEffect(() => {
    setValue('conclusion', review?.conclusion ?? '');
    setValue('score', review?.score ?? '');
  }, [review]);
  // #endregion

  // #region Events
  const onSubmit = async (data) => {
    const formData = new FormData();
    const newData = { ...data, councilId, projectProposalId, councilMemberId };
    Object.keys(newData).forEach((key) => formData.append(key, newData[key]));

    if (isCreateMode) {
      mutateCreate(formData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('council.review.createSuccess'));
          refetchFinancial();
          refetchCouncil();
          setVisible(false);
          reset({}, { keepValues: true });
        },
        onError: async (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      mutateUpdate(formData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('council.review.updateSuccess'));
          refetchFinancial();
          refetchCouncil();
          setVisible(false);
          reset({}, { keepValues: true });
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    }
  };
  // #endregion

  return (
    <Dialog
      header={isCreateMode ? t('council.review.create') : t('council.review.update')}
      visible={visible}
      draggable={false}
      style={{ maxWidth: '600px' }}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.code === 'Enter') e.preventDefault();
          }}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid mb-2 custom-council-review-form">
            <div className="col-12">
              <HugerteEditor
                label={t('council.review.conclusion')}
                name="conclusion"
                height="200px"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12 sm:col-2 md:col-2 lg:col-2">
              <NumberInput
                label={t('council.view.score')}
                name="score"
                inputField="value"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-12 sm:col-10 md:col-10 lg:col-10">
              <FileInput
                label={t('council.review.financialAssessmentForm')}
                name="reportFile"
                control={control}
                errors={errors}
                accept=".pdf"
                acceptOnly="pdfOnly"
                isRequired={isCreateMode}
              />
              {review?.reportFileUrl && (
                <span
                  onClick={() =>
                    handleDownloadReviewFile(
                      councilMemberId,
                      projectProposalId,
                      councilId,
                      review?.reportFileUrl
                    )
                  }
                  className="p-button-text p-button"
                >
                  <i className="pi pi-file-pdf text-red-500 text-xl" />
                  <span className="text-blue-500 underline font-italic text-base">
                    {formatFileName(review?.reportFileUrl)}
                  </span>
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-content-end gap-2">
            <div className="flex-grow-1">
              <Button
                label={t('formLayout.action.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            {!isCreateMode && (
              <div className="flex-grow-1">
                <Button
                  label={t('formLayout.submit')}
                  className="bg-green-600 border-green-600 hover:bg-green-700"
                  type="button"
                  onClick={() => setIsConfirmSubmitModalOpen(true)}
                />
              </div>
            )}
            <div className="flex-grow-1">
              <Button
                label={t('formLayout.action.save')}
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
                disabled={!(isDirty && Object.keys(dirtyFields).length)}
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
}

FinancialForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  isCreateMode: PropTypes.bool.isRequired,
  councilMemberId: PropTypes.string.isRequired,
  councilId: PropTypes.string.isRequired,
  projectProposalId: PropTypes.string.isRequired,
  handleDownloadReviewFile: PropTypes.func.isRequired,
  setIsConfirmSubmitModalOpen: PropTypes.func.isRequired,
  refetchFinancial: PropTypes.func.isRequired,
  refetchCouncil: PropTypes.func.isRequired,
};
